import { forwardRef, type PropsWithChildren } from 'react';

import { cva } from 'cva';

import { Button } from '../button/button';
import { Heading, type HeadingProps } from '../heading/heading';
import { Icon } from '../icon/icon';
import { Sheet } from '../sheet/sheet';
import { Stack } from '../stack/stack';
import { Text, type TextProps } from '../text/text';

import styles from './alert.module.css';

export const alertVariants = cva({
  base: styles.alert,
  variants: {
    variant: {
      success: styles.success,
      warning: styles.warning,
      error: styles.error,
    },
  },

  defaultVariants: {
    variant: 'warning',
  },
});

function AlertIcon(props: { variant: 'success' | 'warning' | 'error' }) {
  const { variant } = props;
  switch (variant) {
    case 'success':
      return <Icon name="message_success" className={styles.alert_icon} />;
    case 'error':
      return <Icon name="message_error" className={styles.alert_icon} />;
    case 'warning':
      return <Icon name="message_warning" className={styles.alert_icon} />;
    default:
  }
}

export type AlertProps = PropsWithChildren<{
  variant?: 'success' | 'warning' | 'error';
  className?: string;
  onDismiss?: () => void;
}>;

export const AlertTitle = (props: HeadingProps) => (
  <Heading level={3} color="inherit" {...props} />
);

export const AlertDescription = (props: TextProps) => (
  <Text variant="body" color="inherit" {...props} />
);

/**
 * Alert - Display important messages to users.
 *
 * ```tsx
 * <Alert variant="error">
 *    <AlertTitle>Error Alert</AlertTitle>
 *    <AlertDescription>This is an error alert description.</AlertDescription>
 * </Alert>
 * ```
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ children, variant = 'warning', className, onDismiss, ...props }, ref) => {
    const classes = alertVariants({
      variant,
      className,
    });

    return (
      <Sheet
        ref={ref}
        variant="ghost"
        spacing="s"
        role="alert"
        {...props}
        className={classes}
      >
        <Stack direction="row" gap="m" alignItems="center">
          <AlertIcon variant={variant} />
          <Stack gap="s" direction="column">
            {children}
          </Stack>
          {onDismiss ? (
            <Button
              icon={<Icon name="close" />}
              aria-label="Close"
              variant="tertiary"
              round
              className={styles.dismiss_button}
              onPress={onDismiss}
            />
          ) : null}
        </Stack>
      </Sheet>
    );
  },
);
